"use client";

import { useState, useEffect, useRef } from "react";
import { createElement } from "react";
import type { ReactNode } from "react";

import type { Bank } from "../types/Bank";

const BANKS_CACHE_KEY = "banksCache";

export type BankSelectOption = {
  label: string;
  value: string;
  /**
   * Optional leading element rendered inside the dropdown item and trigger.
   * Used to display the bank logo.
   */
  leading?: ReactNode;
};

type UseBanksResult = {
  /**
   * Bank options mapped for use in a Select component.
   * Includes an "Andere geldverstrekker" / "Other lender" option at the end.
   */
  bankOptions: BankSelectOption[];
  /**
   * Whether the banks are currently being fetched.
   */
  loading: boolean;
};

type UseBanksParams = {
  /**
   * Async function that resolves to an array of banks.
   * When absent, `bankOptions` stays empty.
   */
  getBanks?: () => Promise<Bank[]>;
  /**
   * Label for the fallback "other" option appended after the bank list.
   */
  otherLabel: string;
};

/**
 * Maps a `Bank` array to `BankSelectOption` objects, reconstructing the
 * non-serialisable `leading` ReactNode from the stored image URL.
 */
const mapBanksToOptions = (
  banks: Bank[],
  otherLabel: string,
): BankSelectOption[] => [
  ...banks.map((bank) => ({
    label: bank.name,
    value: bank.name,
    leading: bank.image
      ? createElement("img", {
          src: bank.image,
          alt: `${bank.name} logo`,
          style: {
            width: 40,
            height: 16,
            objectFit: "contain",
            marginRight: 6,
            filter: "grayscale(100%)",
          },
        })
      : undefined,
  })),
  { label: otherLabel, value: "other" },
];

const readCachedBanks = (): Bank[] | null => {
  try {
    const raw = localStorage.getItem(BANKS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Bank[]) : null;
  } catch {
    return null;
  }
};

const writeCachedBanks = (banks: Bank[]): void => {
  try {
    localStorage.setItem(BANKS_CACHE_KEY, JSON.stringify(banks));
  } catch {
    // localStorage may be unavailable (e.g. private browsing quota exceeded)
  }
};

/**
 * Fetches lenders from the provided `getBanks` callback and maps them
 * to `SelectOption` objects. Appends an "other" option at the end.
 * Persists the raw bank list to localStorage so subsequent mounts render
 * the list immediately without waiting for the network.
 */
export const useBanks = ({
  getBanks,
  otherLabel,
}: UseBanksParams): UseBanksResult => {
  const [bankOptions, setBankOptions] = useState<BankSelectOption[]>(() => {
    const cached = readCachedBanks();
    return cached ? mapBanksToOptions(cached, otherLabel) : [];
  });
  const [loading, setLoading] = useState(() => {
    // Start in loading state only when a fetch will be triggered (getBanks is
    // provided) but no cached data exists yet. When cached data is available,
    // we already have options to display so loading can start as false.
    return !!getBanks && !readCachedBanks();
  });
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!getBanks || hasFetchedRef.current) return;

    hasFetchedRef.current = true;

    getBanks()
      .then((banks) => {
        writeCachedBanks(banks);
        setBankOptions(mapBanksToOptions(banks, otherLabel));
      })
      .catch(() => {
        setBankOptions([{ label: otherLabel, value: "other" }]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getBanks, otherLabel]);

  return { bankOptions, loading };
};
